import bindAllMethods from '../../../utils/bindAllMethods';
import { IEventService } from '../../../services/eventService';
import { INavigationService } from '../../../services/navigationService';
import { LOGIN_PATHNAME } from '../../../services/session/constants';
import { InitialNavigationStateRepository } from './InitialNavigationStateRepository';
import { ITenantHandlerService } from '../../../services/tenantHandler';
import { ISessionService } from '../../../services/session';
import { IContainer } from '../../../containers';
import { internalLogger } from '../../../interface/v1/clientOsLogger';

export type RefreshWhenNativeAuthStatusChangedParams = {
  authRefreshContainer: IContainer;
  eventService: IEventService;
  navigationService: INavigationService;
  initialNavigationStateRepository: InitialNavigationStateRepository;
  tenantHandlerService: ITenantHandlerService;
  sessionService: ISessionService;
  refreshScreenOnChange: boolean;
  nativeRefreshServicesOnAuthChange: boolean;
};

const AuthPluginLoginChangeEventName = 'com.hp.jarvis.auth.event.login.change';

export default class SubscriberRefreshWhenNativeAuthStatusChanged {
  private _eventService: IEventService;
  private _navigationService: INavigationService;
  private _tenantHandlerService: ITenantHandlerService;
  private _sessionService: ISessionService;
  private _authRefreshContainer: IContainer;
  private _initialNavigationStateRepository: InitialNavigationStateRepository;
  private _refreshScreenOnChange: boolean;
  private _nativeRefreshServicesOnAuthChange: boolean;

  constructor({
    authRefreshContainer,
    refreshScreenOnChange,
    eventService,
    navigationService,
    initialNavigationStateRepository,
    tenantHandlerService,
    sessionService,
    nativeRefreshServicesOnAuthChange
  }: RefreshWhenNativeAuthStatusChangedParams) {
    bindAllMethods(this);
    this._authRefreshContainer = authRefreshContainer;
    this._refreshScreenOnChange = refreshScreenOnChange;
    this._nativeRefreshServicesOnAuthChange = nativeRefreshServicesOnAuthChange;
    this._eventService = eventService;
    this._navigationService = navigationService;
    this._initialNavigationStateRepository = initialNavigationStateRepository;
    this._tenantHandlerService = tenantHandlerService;
    this._sessionService = sessionService;
  }

  public init = async (): Promise<void> => {
    /** If the nativeRefreshListener was disabled on Manifest,
     * we don't need to subscribe to the event.
     */
    if (!this._refreshScreenOnChange) {
      internalLogger?.debug?.(
        'Native refresh listener is disabled, not subscribing to auth change events.'
      );
      return;
    }

    this._consumeSavedNavigationState();

    void this._eventService.subscribe(
      AuthPluginLoginChangeEventName,
      async (event) => {
        const { eventData } = event || {};
        const {
          currentPath,
          hasUserLoggedInOrLoggedoutFromJWeb,
          hasUserLoggedInOrLoggedoutFromShell
        } = this._checkStates(eventData);

        const oldTenant = eventData?.previousAccount?.tenantId;
        const newTenant = eventData?.currentAccount?.tenantId;

        // Enter here when logging out as well;
        if (
          currentPath !== LOGIN_PATHNAME &&
          (hasUserLoggedInOrLoggedoutFromJWeb ||
            hasUserLoggedInOrLoggedoutFromShell)
        ) {
          if (this._nativeRefreshServicesOnAuthChange) {
            // New Behavior
            internalLogger?.debug?.(
              'Native refresh listener is enabled, refreshing auth container.'
            );

            await this._authRefreshContainer.refreshContext();
            return;
          } else {
            // Old Behavior
            internalLogger?.debug?.(
              'Native refresh listener is disabled, reloading page while keeping state.'
            );
            this._reloadPageKeepingState();
            return;
          }
        }

        const hasTenantChanged = newTenant !== oldTenant;
        if (newTenant && hasTenantChanged) {
          this._tenantHandlerService.setTenant(
            newTenant,
            this._tenantHandlerService.START_TENANT_LEVEL,
            { reload: false }
          );
        }
      }
    );
  };

  private _reloadPageKeepingState = (): void => {
    const currentState = this._navigationService.location.state;
    const currentPath = this._navigationService.location.pathname;
    this._initialNavigationStateRepository.save(currentPath, currentState);
    window.location.reload();
  };

  private _consumeSavedNavigationState = (): void => {
    const currentPath = this._navigationService.location.pathname;
    const savedState =
      this._initialNavigationStateRepository.findOne(currentPath);
    if (savedState) {
      this._initialNavigationStateRepository.clear();
      this._navigationService.replace({
        ...this._navigationService.location,
        state: savedState
      });
    }
  };

  private _checkStates(eventData: any) {
    const currentPath = this._navigationService.location.pathname;
    const hasCurrentEmail = !!eventData?.currentAccount?.emailAddress;
    const hasPreviousEmail = !!eventData?.previousAccount?.emailAddress;

    const isLoggedIn = this._sessionService.isLoggedIn();

    const hasUserLoggedInOrLoggedoutFromJWeb =
      !hasCurrentEmail || !hasPreviousEmail;

    const hasUserLoggedInOrLoggedoutFromShell =
      (!isLoggedIn && hasCurrentEmail) || (isLoggedIn && !hasCurrentEmail);
    return {
      currentPath,
      hasUserLoggedInOrLoggedoutFromJWeb,
      hasUserLoggedInOrLoggedoutFromShell
    };
  }
}
